import { motion } from 'framer-motion';
import { Lightbulb, Hammer, Building2, Target, Leaf, Heart, RefreshCw, Smartphone, ArrowRight, Linkedin, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const foundationPrinciples = [
  {
    icon: Lightbulb,
    title: "Encouraging Idea Creation and Discussion",
    description: "At CfE, we believe that great ventures start with great conversations. We normalize discussions around problems, ideas, and potential solutions, making entrepreneurship a natural extension of campus life."
  },
  {
    icon: Hammer,
    title: "Inspiring Builders, Not Just Thinkers",
    description: "While ideation is important, CfE emphasizes going beyond ideas to action. We encourage students to develop tangible products, services, and ventures through experimentation, iteration, and execution."
  },
  {
    icon: Building2,
    title: "Building a Long-Term Institutional Platform",
    description: "Unlike short-term student clubs, CfE functions as a sustained institutional platform. It supports entrepreneurship across multiple cohorts and is open to Krea students, alumni, and professors for interdisciplinary collaboration."
  }
];

const philosophyPillars = [
  {
    icon: Target,
    title: "Choosing the Right Problem",
    description: "Problems should be meaningful, widely experienced, and economically viable to solve. There should be an identifiable opportunity and real demand."
  },
  {
    icon: Leaf,
    title: "Building Sustainable Solutions",
    description: "Solutions should be cost-effective, profitable, scalable, and sustainable over time. The aim is to build enduring businesses, not quick fixes."
  },
  {
    icon: Heart,
    title: "Balancing Passion, Interest, and Economics",
    description: "Effective ventures lie at the intersection of personal passion, intellectual curiosity, and economic opportunity. This balance ensures commitment and long-term viability."
  },
  {
    icon: RefreshCw,
    title: "Adaptability",
    description: "Entrepreneurs must be flexible and willing to pivot based on feedback and emerging trends. Rigidity is the enemy of growth."
  },
  {
    icon: Smartphone,
    title: "Leveraging Digital Platforms",
    description: "Digital platforms are essential for customer acquisition, brand building, and community engagement. Embracing technology is non-negotiable."
  }
];

const timelineEvents = [
  {
    year: "~3 Years Ago",
    title: "The Beginning",
    description: "A small group of passionate Krea University students approached university leadership with a vision to create a structured entrepreneurship platform."
  },
  {
    year: "Early Milestone",
    title: "Zero-to-One",
    description: "The first pitching event where students presented early-stage ideas and received mentor feedback, moving from concept to prototype."
  },
  {
    year: "Growth",
    title: "Confluence",
    description: "A flagship event hosted in Chennai at the Hilton, bringing together founders, investors, and students — connecting Krea's community with the broader startup ecosystem."
  },
  {
    year: "Evolution",
    title: "Formalization",
    description: "More students joined; events and programs were formalized; the structure evolved from an informal initiative to an organized centre."
  },
  {
    year: "Today",
    title: "Operational Excellence",
    description: "CfE operates with a core team of approximately 11 members managing events, partnerships, and ecosystem development on campus."
  }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-[#F3F4F6] relative overflow-hidden" data-testid="about-hero">
        <div className="absolute top-0 right-0 w-1/2 h-full subtle-gradient opacity-50" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Our Story
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-6xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
              data-testid="about-page-heading"
            >
              About the Centre for Entrepreneurship
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-[#58595B] leading-relaxed"
            >
              From a student initiative to an institutional platform for venture creation.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-padding bg-white" data-testid="vision-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="text-[#075FA3] font-semibold text-sm tracking-wider uppercase mb-4 block"
              >
                Our Vision
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold text-[#231F20] mb-8 font-['Bricolage_Grotesque']"
              >
                A Centre for Building
              </motion.h2>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="space-y-6 text-[#58595B] leading-relaxed text-lg"
              >
                <p>
                  The Centre for Entrepreneurship (CfE) at Krea University was established to 
                  cultivate an entrepreneurial culture on campus where ideas are openly discussed, 
                  tested, and transformed into real ventures.
                </p>
                <p>
                  At its core, CfE is designed to be a centre for building — not merely a student 
                  club or society. The goal is to create an ecosystem where students, alumni, and 
                  faculty members feel empowered to experiment with ideas, collaborate with 
                  like-minded individuals, and pursue entrepreneurship as a serious pathway.
                </p>
                <p className="text-[#075FA3] font-medium">
                  The centre aims to become a place where individuals with curiosity, ambition, 
                  and initiative come together to build solutions that are scalable, sustainable, 
                  and impactful.
                </p>
              </motion.div>
            </div>
            <div className="space-y-6">
              {foundationPrinciples.map((principle, index) => (
                <motion.div
                  key={principle.title}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="border-l-4 border-l-[#075FA3] hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#075FA3]/10 flex items-center justify-center flex-shrink-0">
                          <principle.icon className="w-6 h-6 text-[#075FA3]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#231F20] mb-2 font-['Bricolage_Grotesque']">
                            {principle.title}
                          </h3>
                          <p className="text-[#58595B] text-sm leading-relaxed">
                            {principle.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* History Timeline */}
      <section className="section-padding bg-[#F3F4F6]" data-testid="history-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Our Journey
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-[#231F20] font-['Bricolage_Grotesque']"
            >
              The CfE Story
            </motion.h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="relative pl-8 border-l-2 border-[#075FA3]/30">
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={event.title}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative pb-12 last:pb-0"
                >
                  <div className="absolute -left-[25px] w-4 h-4 rounded-full bg-[#075FA3] border-4 border-white" />
                  <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow ml-4">
                    <span className="text-[#4DBDDE] font-semibold text-sm">{event.year}</span>
                    <h3 className="text-xl font-semibold text-[#231F20] mt-2 mb-3 font-['Bricolage_Grotesque']">
                      {event.title}
                    </h3>
                    <p className="text-[#58595B] leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-white" data-testid="philosophy-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#075FA3] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              Our Approach
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
            >
              Entrepreneurship Philosophy
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-[#58595B] leading-relaxed"
            >
              At CfE, entrepreneurship is framed not just as company creation but as a 
              problem-solving mindset. Here are the core elements we believe make a strong startup:
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {philosophyPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 bg-[#F3F4F6] hover:bg-white hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-2xl bg-[#C83041]/10 flex items-center justify-center mb-6 group-hover:bg-[#C83041] transition-colors">
                      <pillar.icon className="w-7 h-7 text-[#C83041] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#231F20] mb-3 font-['Bricolage_Grotesque']">
                      {pillar.title}
                    </h3>
                    <p className="text-[#58595B] leading-relaxed">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Long-Term Goal */}
      <section className="py-20 bg-[#075FA3]" data-testid="long-term-goal-section">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#4DBDDE] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              The Future
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-white mb-8 font-['Bricolage_Grotesque']"
            >
              The Long-Term Vision
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 leading-relaxed"
            >
              The long-term ambition of CfE is to transform Krea University into a campus 
              where ideas are constantly discussed, students regularly launch ventures, and 
              entrepreneurship becomes a mainstream career path. By combining student initiative 
              and institutional support, CfE aims to build a sustainable startup ecosystem that 
              produces founders, innovators, and problem-solvers capable of creating meaningful impact.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-white" data-testid="team-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.span
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-[#C83041] font-semibold text-sm tracking-wider uppercase mb-4 block"
            >
              The People
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-[#231F20] mb-6 font-['Bricolage_Grotesque']"
            >
              The CfE Core Team
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-lg text-[#58595B]"
            >
              Meet the dedicated individuals driving CfE's mission forward.
            </motion.p>
          </div>

          {/* Overall Head */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-4 shadow-lg">
                <img src="https://customer-assets.emergentagent.com/job_5ac75a11-ace0-4082-ab6e-a0c86caed3f6/artifacts/rz0wa7m8_Headshot.png" alt="Rohan Singhi" className="w-full h-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Rohan Singhi</h3>
              <p className="text-[#C83041] font-semibold">Overall Head</p>
            </div>
          </motion.div>

          {/* Department Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Marketing & Promotions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border-t-4 border-t-[#C83041] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-[#075FA3] mb-4 font-['Bricolage_Grotesque']">
                    Marketing & Promotions
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/c30ii92e_Bolt-Headshot%20%281%29.jpg" alt="Alaika Bansal" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#231F20]">Alaika Bansal</p>
                        <p className="text-sm text-[#C83041]">Head</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/jv5jpxrf_Jun%2015%2C%202026%2C%2009_52_08%20PM.png" alt="Abeer Shaikh" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#231F20]">Abeer Shaikh</p>
                        <p className="text-sm text-[#58595B]">Team Member</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/auu40tio_Tanisha-Headshot.jpeg" alt="Tanisha Senthilkumar" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#231F20]">Tanisha Senthilkumar</p>
                        <p className="text-sm text-[#58595B]">Team Member</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Investor Relations */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Card className="h-full border-t-4 border-t-[#075FA3] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-[#075FA3] mb-4 font-['Bricolage_Grotesque']">
                    Investor Relations
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/34cget0l_Tulika-Headshot%20%281%29.jpg" alt="Tulika Biswas" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#231F20]">Tulika Biswas</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/17eyh0up_Ananyaa-Headshot.jpeg" alt="Ananyaa Premanand" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#231F20]">Ananyaa Premanand</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Events & Logistics */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Card className="h-full border-t-4 border-t-[#389FCF] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-[#075FA3] mb-4 font-['Bricolage_Grotesque']">
                    Events & Logistics
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/kt5ktfpg_Saanvi-Headshot%20%281%29.jpg" alt="Saanvi Razdan" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#231F20]">Saanvi Razdan</p>
                        <p className="text-sm text-[#389FCF]">Head</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/rd14fhvs_WhatsApp%20Image%202026-06-15%20at%2022.22.11.jpeg" alt="Arnav Bajoria" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#231F20]">Arnav Bajoria</p>
                        <p className="text-sm text-[#58595B]">Team Member</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/jh4wy91x_Aryan-Headshot%20%281%29.jpg" alt="Aryan Karthik" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#231F20]">Aryan Karthik</p>
                        <p className="text-sm text-[#58595B]">Team Member</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/rcnz98gz_Anushka-Headshot%20%281%29.jpg" alt="Anushka Jha" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-medium text-[#231F20]">Anushka Jha</p>
                        <p className="text-sm text-[#58595B]">Team Member</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Finance */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <Card className="h-full border-t-4 border-t-[#0077B6] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h4 className="text-lg font-bold text-[#075FA3] mb-4 font-['Bricolage_Grotesque']">
                    Finance
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden">
                        <img src="https://customer-assets.emergentagent.com/job_b59cf08e-86a7-4284-a0e5-16f32bce918a/artifacts/ogyg9ulo_Chirag-Headshot%20%281%29.jpg" alt="Chirag Punjabi" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#231F20]">Chirag Punjabi</p>
                        <p className="text-sm text-[#0077B6]">Head</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#C83041]">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Bricolage_Grotesque']"
            >
              Join the CfE Community
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 mb-10"
            >
              Whether you're a student with an idea, an alumni looking to mentor, or a faculty 
              member interested in research commercialization — CfE is for you.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-wrap justify-center gap-4"
            >
              <Link to="/register" data-testid="about-register-btn">
                <Button className="bg-white text-[#C83041] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
                  Register Your Startup
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/advisors" data-testid="about-book-advisor-btn">
                <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#C83041] rounded-full px-8 py-6 text-lg font-semibold transition-all">
                  Book an Advisor
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
